// client/src/pages/Search.js
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import SearchBar from '../components/search/SearchBar';
import FilterChips from '../components/search/FilterChips';
import FilterSheet from '../components/search/FilterSheet';
import CategoryRows from '../components/search/CategoryRows';
import SearchResults from '../components/search/SearchResults';
import { trackSearchEvent, trackFilterChange, trackPredefinedFilter, trackEvent, EVENTS } from '../utils/analytics';
import { sortStocks } from '../utils/sorting';
import config from '../config/config';
import { toast } from '../components/Toast';
import '../styles/Search.css';
import '../styles/BaseComponents.css';

const Search = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  // States
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });
  const [error, setError] = useState(null);
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [sortBy, setSortBy] = useState('market_cap');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeFilter, setActiveFilter] = useState(null);

  // Auto-suggest states
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [isSearchingSuggestions, setIsSearchingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const abortControllerRef = useRef(null);
  const scrollContainerRefs = useRef({});

  // Modal states
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Add-to-Portfolio Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStockForAdd, setSelectedStockForAdd] = useState(null);
  const [addQuantity, setAddQuantity] = useState(1);
  const [addPurchaseDate, setAddPurchaseDate] = useState(() => {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return oneYearAgo.toISOString().split('T')[0];
  });
  const [isAddingStock, setIsAddingStock] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    sector: 'All',
    country: 'All',
    market_cap: { value: 0, label: 'Marktkapitalisierung', unit: 'B', max: 1000, isMax: false },
    trailing_p_e: { value: 0, label: 'KGV', unit: '', max: 100, isMax: true },
    forward_p_e: { value: 0, label: 'KGV erwartet', unit: '', max: 100, isMax: true },
    price_to_book: { value: 0, label: 'KBV', unit: '', max: 20, isMax: true },
    enterprise_to_ebitda: { value: 0, label: 'Wert/EBITDA', unit: '', max: 50, isMax: true },
    dividend: { value: 0, label: 'Dividende pro Aktie', unit: '', max: 10, isMax: false },
    dividend_yield: { value: 0, label: 'Dividendenrendite', unit: '%', max: 20, isMax: false },
    profit_margin: { value: 0, label: 'Gewinnmarge', unit: '%', max: 100, isMax: false },
    operating_margin: { value: 0, label: 'Operative Marge', unit: '%', max: 100, isMax: false },
    next_year_growth: { value: 0, label: 'Erw. Wachstum (Jahr)', unit: '%', max: 100, isMax: false },
    revenue_growth: { value: 0, label: 'Umsatzwachstum', unit: '%', max: 100, isMax: false },
    earnings_growth: { value: 0, label: 'Gewinnwachstum', unit: '%', max: 100, isMax: false }
  });

  const [collapsedSections, setCollapsedSections] = useState({
    valuation: false,
    income: true,
    growth: true
  });

  // Category states
  const [categoryStocks, setCategoryStocks] = useState({});
  const [currentCategory, setCurrentCategory] = useState(null);

  // Predefined filters
  const predefinedFilters = [
    { id: 'high-dividend', label: 'Dividendenstars', filters: { dividend_yield: 3.5, market_cap: 1 } },
    { id: 'growth-stocks', label: 'Wachstumsaktien', filters: { revenue_growth: 15, earnings_growth: 10, next_year_growth: 10 } },
    { id: 'value-stocks', label: 'Value-Aktien', filters: { trailing_p_e: 15, price_to_book: 2.5, dividend_yield: 2 } },
    { id: 'high-profit-margin', label: 'Hohe Margen', filters: { profit_margin: 25, operating_margin: 30 } },
    { id: 'undervalued-growth', label: 'Unterbewertete Wachstumswerte', filters: { forward_p_e: 20, revenue_growth: 20, price_to_book: 3 } }
  ];

  // Category definitions
  const categories = [
    { id: 'undervalued_gems', title: '💎 Günstige Bewertung', description: 'Aktien mit niedrigem KGV', filters: { trailing_p_e: 10 }, valueKey: 'trailing_p_e', sortBy: 'trailing_p_e', sortOrder: 'asc' },
    { id: 'dividend_aristocrats', title: '🏆 Hohe Dividende', description: 'Aktien mit hoher und stabiler Dividendenrendite', filters: { dividend_yield: 4 }, valueKey: 'dividend_yield', sortBy: 'dividend_yield', sortOrder: 'desc' },
    { id: 'daily_winners', title: '🚀 Wochengewinner', description: 'Aktien mit der besten Performance der letzten 7 Tage', filters: {}, valueKey: 'weekly_change', sortBy: 'weekly_change', sortOrder: 'desc' },
    { id: 'growth_value', title: '📈 Wachstum + Wert', description: 'Gutes Wachstum bei fairer Bewertung', filters: { next_year_growth: 10, trailing_p_e: 25 }, valueKey: 'next_year_growth', sortBy: 'next_year_growth', sortOrder: 'desc' },
    { id: 'high_margin', title: '🛡️ Hohe Profitabilität', description: 'Aktien mit hoher Gewinnmarge', filters: { profit_margin: 15 }, valueKey: 'profit_margin', sortBy: 'profit_margin', sortOrder: 'desc' }
  ];

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (formData.country !== 'All') count++;
    if (formData.sector !== 'All') count++;
    Object.entries(formData).forEach(([key, cfg]) => {
      if (typeof cfg === 'object' && cfg.value > 0) count++;
    });
    return count;
  };

  // Auto-suggest for stock names with debounce
  const debounceTimerRef = useRef(null);

  const fetchSuggestions = useCallback(async (value) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsSearchingSuggestions(true);

    try {
      const response = await fetch(`${config.apiUrl}/api/stocks/search-by-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: value }),
        signal: abortController.signal
      });

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      if (data.success && data.data) {
        setNameSuggestions(data.data.slice(0, 8));
        setShowSuggestions(true);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Name search error:', err);
      }
    } finally {
      setIsSearchingSuggestions(false);
    }
  }, []);

  const handleNameSearch = useCallback((value) => {
    setFormData(prev => ({ ...prev, name: value }));

    if (value.length < 2) {
      setNameSuggestions([]);
      setShowSuggestions(false);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      return;
    }

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(value), 350);
  }, [fetchSuggestions]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleSuggestionSelect = (stock) => {
    const updatedFormData = { ...formData, name: stock.name };
    setFormData(updatedFormData);
    setShowSuggestions(false);
    setNameSuggestions([]);
    handleSubmitWithFormData(updatedFormData);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Portfolio add handlers
  const handleAddToPortfolio = (stock) => {
    if (!token) {
      toast.info('Bitte melde dich an, um Aktien zu deinem Portfolio hinzuzufügen.');
      navigate('/login');
      return;
    }
    setSelectedStockForAdd(stock);
    setAddQuantity(1);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    setAddPurchaseDate(oneYearAgo.toISOString().split('T')[0]);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setSelectedStockForAdd(null);
    setAddQuantity(1);
  };

  const confirmAddToPortfolio = async () => {
    if (!selectedStockForAdd || addQuantity <= 0) return;
    setIsAddingStock(true);

    try {
      const response = await fetch(`${config.apiUrl}/api/portfolio/${selectedStockForAdd.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quantity: Number(addQuantity),
          purchase_date: addPurchaseDate,
          trackTransaction: true
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      trackEvent(EVENTS.ADD_TO_PORTFOLIO, {
        stock_id: selectedStockForAdd.id,
        stock_name: selectedStockForAdd.name,
        quantity: addQuantity,
        purchase_date: addPurchaseDate,
        from_search: true
      });

      toast.success(`${selectedStockForAdd.name} wurde zum Portfolio hinzugefügt!`);
      closeAddModal();
    } catch (error) {
      console.error('Failed to add to portfolio:', error);
      toast.error('Fehler beim Hinzufügen zum Portfolio. Bitte versuche es erneut.');
    } finally {
      setIsAddingStock(false);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showFilterModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [showFilterModal]);

  // Load category stocks on mount and whenever the currency changes
  useEffect(() => {
    loadCategoryStocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredCurrency]);

  // Fetch preferred currency
  useEffect(() => {
    const fetchPreferredCurrency = async () => {
      if (token) {
        try {
          const response = await fetch(config.apiUrl + '/api/portfolio/preferred-currency', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!response.ok) return;
          const data = await response.json();
          if (data && data.currency) setPreferredCurrency(data.currency);
        } catch (error) {
          console.error('Error fetching preferred currency:', error);
        }
      }
    };
    fetchPreferredCurrency();
  }, [token]);

  // Load stocks for categories
  const loadCategoryStocks = async () => {
    const categoryPromises = categories.map(async (category) => {
      try {
        const response = await fetch(`${config.apiUrl}/api/stocks/search/categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categoryId: category.id, currency: preferredCurrency, limit: 15 })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const basicResults = data.data.slice(0, 12);
            const stockIds = basicResults.map(stock => stock.id);
            const detailedResults = await fetchStockDetailsBatch(stockIds);
            return { categoryId: category.id, stocks: detailedResults.slice(0, 10) };
          }
        }
        return { categoryId: category.id, stocks: [] };
      } catch (error) {
        console.error(`Error loading ${category.id}:`, error);
        return { categoryId: category.id, stocks: [] };
      }
    });

    const results = await Promise.all(categoryPromises);
    const categoryData = {};
    results.forEach(result => { categoryData[result.categoryId] = result.stocks; });
    setCategoryStocks(categoryData);
  };

  // Batch stock detail fetcher - fetches multiple stocks in one request
  const fetchStockDetailsBatch = async (stockIds) => {
    if (!stockIds || stockIds.length === 0) return [];
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(`${config.apiUrl}/api/stocks/batch`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids: stockIds, currency: preferredCurrency })
      });
      if (!response.ok) return [];
      const data = await response.json();
      return data.success ? data.data : [];
    } catch (err) {
      console.error('Batch fetch error:', err);
      return [];
    }
  };

  // Scroll category rows
  const scrollCategory = (categoryId, direction) => {
    const container = scrollContainerRefs.current[categoryId];
    if (!container) return;
    const scrollAmount = 340;
    container.scrollTo({
      left: container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount),
      behavior: 'smooth'
    });
  };

  // Toggle filter section
  const toggleSection = (section) => {
    trackEvent(EVENTS.FILTER_SECTION_TOGGLE, {
      section_name: section,
      new_state: !collapsedSections[section] ? 'expanded' : 'collapsed'
    });
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Apply predefined filter and immediately search
  const applyPredefinedFilter = (filter) => {
    if (activeFilter && activeFilter.id === filter.id) {
      resetFilters();
      setActiveFilter(null);
      setSearchInitiated(false);
      setSearchResults([]);
      return;
    }

    setActiveFilter(filter);

    trackPredefinedFilter({
      filterId: filter.id,
      filterName: filter.label,
      filterCount: Object.keys(filter.filters).length,
      replacesExisting: !!activeFilter
    });

    const newFormData = {...formData};
    Object.keys(newFormData).forEach(key => {
      if (typeof newFormData[key] === 'object' && newFormData[key].hasOwnProperty('value')) {
        newFormData[key].value = 0;
      }
    });

    Object.entries(filter.filters).forEach(([key, value]) => {
      if (newFormData[key]) {
        newFormData[key].value = filter.filters[key];
      }
    });

    setFormData(newFormData);

    // Immediately trigger search with the new filters
    setTimeout(() => handleSubmitWithFormData(newFormData), 50);
  };

  // Handle input change in filter modal
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'name' || name === 'sector' || name === 'country') {
      setFormData(prev => ({ ...prev, [name]: value }));
      if ((name === 'sector' || name === 'country') && value !== formData[name]) {
        trackEvent(EVENTS.FILTER_CHANGE, { filter_name: name, old_value: formData[name], new_value: value });
      }
    } else {
      const oldValue = formData[name].value;
      const newValue = parseFloat(value);
      setFormData(prev => ({
        ...prev,
        [name]: { ...prev[name], value: newValue }
      }));

      if (Math.abs(newValue - oldValue) > 0.1) {
        trackFilterChange({
          filter_name: name, filter_value: newValue, filter_previous: oldValue,
          filter_category: formData[name]?.label || name, filter_unit: formData[name]?.unit || '',
          filter_source: "manual_adjustment"
        });
      }
    }

    if (activeFilter) {
      trackEvent(EVENTS.FILTER_PRESET_CANCELED, {
        filter_id: activeFilter.id, filter_name: activeFilter.label, changed_field: name
      });
      setActiveFilter(null);
    }
  };

  // Reset filters
  const resetFilters = () => {
    trackEvent(EVENTS.FILTER_RESET, { previous_filter_count: getActiveFilterCount() });
    const newFormData = {...formData};
    newFormData.name = '';
    newFormData.sector = 'All';
    newFormData.country = 'All';
    Object.keys(newFormData).forEach(key => {
      if (typeof newFormData[key] === 'object' && newFormData[key].hasOwnProperty('value')) {
        newFormData[key].value = 0;
      }
    });
    setFormData(newFormData);
    setActiveFilter(null);
  };

  // Remove a single filter
  const handleRemoveFilter = (key) => {
    if (key === 'preset') {
      resetFilters();
      setActiveFilter(null);
      setSearchInitiated(false);
      setSearchResults([]);
      return;
    }

    const newFormData = {...formData};
    if (key === 'country') {
      newFormData.country = 'All';
    } else if (key === 'sector') {
      newFormData.sector = 'All';
    } else if (newFormData[key] && typeof newFormData[key] === 'object') {
      newFormData[key] = { ...newFormData[key], value: 0 };
    }

    setFormData(newFormData);
    setActiveFilter(null);

    // Check if any filters remain - if not, go back to categories
    let hasFilters = newFormData.country !== 'All' || newFormData.sector !== 'All';
    if (!hasFilters) {
      Object.entries(newFormData).forEach(([k, cfg]) => {
        if (typeof cfg === 'object' && cfg.value > 0) hasFilters = true;
      });
    }

    if (!hasFilters) {
      setSearchInitiated(false);
      setSearchResults([]);
    } else {
      setTimeout(() => handleSubmitWithFormData(newFormData), 50);
    }
  };

  // Submit search with specific formData (for immediate searches after filter changes)
  const handleSubmitWithFormData = async (fd) => {
    setLoading(true);
    setError(null);
    setSearchInitiated(true);
    setShowFilterModal(false);
    setShowSuggestions(false);
    setNameSuggestions([]);

    const startTime = Date.now();

    const activeFilters = {};
    Object.entries(fd).forEach(([key, cfg]) => {
      if (typeof cfg === 'object' && cfg.value > 0) activeFilters[key] = cfg.value;
    });

    trackEvent(EVENTS.SEARCH_INITIATED, {
      search_term: fd.name || "[empty]",
      sector: fd.sector,
      currency: preferredCurrency,
      filter_count: Object.keys(activeFilters).length,
      has_filters: Object.keys(activeFilters).length > 0 ? "yes" : "no",
      using_preset: activeFilter ? "yes" : "no",
      preset_name: activeFilter ? activeFilter.label : null
    });

    try {
      const apiSearchParams = {
        name: fd.name,
        country: fd.country,
        sector: fd.sector,
        currency: preferredCurrency
      };
      Object.entries(fd).forEach(([key, cfg]) => {
        if (typeof cfg === 'object' && cfg.value > 0) apiSearchParams[key] = cfg.value;
      });

      const response = await fetch(config.apiUrl + '/api/stocks/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(apiSearchParams)
      });

      if (!response.ok) throw new Error('Netzwerkfehler bei der Suche');

      const result = await response.json();
      if (result.error) throw new Error(result.error);

      const basicResults = result.data || [];
      const queryTimeMs = Date.now() - startTime;

      trackSearchEvent({
        searchTerm: fd.name, sector: fd.sector, activeFilters: fd,
        currency: preferredCurrency, resultsCount: basicResults.length,
        queryTimeMs, sortBy, sortOrder
      });

      // Fetch all stock details in batches via the batch endpoint
      const batchSize = 50;
      const detailedResults = [];

      for (let i = 0; i < basicResults.length; i += batchSize) {
        const batchIds = basicResults.slice(i, i + batchSize).map(stock => stock.id);
        const batchResults = await fetchStockDetailsBatch(batchIds);
        detailedResults.push(...batchResults);
        setSearchResults([...detailedResults]);
        setLoadingProgress({ loaded: detailedResults.length, total: basicResults.length });
      }

      setSearchResults(detailedResults);
      setLoadingProgress({ loaded: 0, total: 0 });
    } catch (error) {
      console.error('Suchfehler:', error);
      setError(error.message);
      trackEvent(EVENTS.API_ERROR, {
        error_type: 'search_error', error_message: error.message,
        search_term: fd.name,
        has_filters: Object.values(fd).some(v => typeof v === 'object' && v.value > 0) ? "yes" : "no"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle submit (from filter modal or form)
  const handleSubmit = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    handleSubmitWithFormData(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, preferredCurrency, token, activeFilter]);

  // Handle category "show all"
  const handleShowAllCategory = async (category) => {
    setCurrentCategory(category);
    setSearchInitiated(true);
    setLoading(true);
    setError(null);
    setSortBy(category.sortBy);
    setSortOrder(category.sortOrder);

    const newFormData = {...formData};
    Object.keys(newFormData).forEach(key => {
      if (typeof newFormData[key] === 'object' && newFormData[key].hasOwnProperty('value')) {
        newFormData[key].value = 0;
      }
    });
    if (category.filters && Object.keys(category.filters).length > 0) {
      Object.entries(category.filters).forEach(([key, value]) => {
        if (newFormData[key]) newFormData[key].value = value;
      });
    }
    setFormData(newFormData);

    try {
      const response = await fetch(`${config.apiUrl}/api/stocks/search/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryId: category.id, currency: preferredCurrency, limit: 100 })
      });

      if (!response.ok) throw new Error('Fehler beim Laden der Kategorie');
      const data = await response.json();
      if (!data.success) throw new Error('Keine Ergebnisse gefunden');

      const basicResults = data.data || [];
      setLoadingProgress({ loaded: 0, total: basicResults.length });

      // Fetch all stock details via the batch endpoint
      const batchSize = 50;
      const detailedResults = [];

      for (let i = 0; i < basicResults.length; i += batchSize) {
        const batchIds = basicResults.slice(i, i + batchSize).map(stock => stock.id);
        const batchResults = await fetchStockDetailsBatch(batchIds);
        detailedResults.push(...batchResults);
        setSearchResults([...detailedResults]);
        setLoadingProgress({ loaded: detailedResults.length, total: basicResults.length });
      }

      setSearchResults(detailedResults);
      setLoadingProgress({ loaded: 0, total: 0 });
    } catch (error) {
      console.error('Error loading category:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Back to categories
  const handleBackToCategories = () => {
    setCurrentCategory(null);
    setSearchInitiated(false);
    resetFilters();
    setSortBy('market_cap');
    setSortOrder('desc');
    setSearchResults([]);
  };

  return (
    <>
      <Helmet>
        <title>Aktiensuche für Value Investoren | DynaStoxx</title>
        <meta name="description" content="Finde unterbewertete Aktien mit unserer fortschrittlichen Suchfunktion. Filtere nach KGV, KBV, Dividendenrendite, Wachstumsprognosen und mehr. Ideal für Value-Investoren." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://dynastoxx.com/search" />
      </Helmet>

      <div className="search-container">
        {/* Breadcrumb when in category view */}
        {currentCategory && (
          <div className="breadcrumb">
            <button className="back-button" onClick={handleBackToCategories}>
              <i className="fas fa-arrow-left"></i> Zurück
            </button>
            <span className="current-category">{currentCategory.title}</span>
          </div>
        )}

        {/* Search Card */}
        <div className="search-card mobile-search-card">
          {!searchInitiated && !currentCategory && (
            <div className="intro-card compact">
              <h2>Aktiensuche</h2>
              <p className="intro-subtitle">Suche nach Name oder nutze die Filter-Presets unten</p>
            </div>
          )}

          <SearchBar
            searchTerm={formData.name}
            onSearchChange={handleNameSearch}
            onSubmit={handleSubmit}
            onClear={() => {
              setFormData(prev => ({ ...prev, name: '' }));
              setNameSuggestions([]);
              setShowSuggestions(false);
            }}
            suggestions={nameSuggestions}
            showSuggestions={showSuggestions}
            isSearching={isSearchingSuggestions}
            onSuggestionSelect={handleSuggestionSelect}
            activeFilterCount={getActiveFilterCount()}
            onFilterClick={() => setShowFilterModal(true)}
            searchInputRef={searchInputRef}
            suggestionsRef={suggestionsRef}
          />

          {/* Preset chips + active filter badges */}
          <FilterChips
            predefinedFilters={predefinedFilters}
            activeFilter={activeFilter}
            onPresetClick={applyPredefinedFilter}
            formData={formData}
            onRemoveFilter={handleRemoveFilter}
          />
        </div>

        {/* Filter Bottom Sheet */}
        <FilterSheet
          isOpen={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          formData={formData}
          onInputChange={handleInputChange}
          collapsedSections={collapsedSections}
          onToggleSection={toggleSection}
          onReset={resetFilters}
          onApply={() => { setShowFilterModal(false); handleSubmit(); }}
          loading={loading}
        />

        {/* Results or Category Rows */}
        {searchInitiated ? (
          <SearchResults
            results={searchResults}
            loading={loading}
            loadingProgress={loadingProgress}
            error={error}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={setSortBy}
            onSortOrderChange={setSortOrder}
            sortStocks={sortStocks}
            currentCategory={currentCategory}
            activeFilterLabel={activeFilter ? activeFilter.label : (currentCategory ? currentCategory.title : (formData.name || null))}
            onRetry={() => currentCategory ? handleShowAllCategory(currentCategory) : handleSubmit()}
            onAddToPortfolio={handleAddToPortfolio}
          />
        ) : (
          <CategoryRows
            categories={categories}
            categoryStocks={categoryStocks}
            onShowAll={handleShowAllCategory}
            onAddToPortfolio={token ? handleAddToPortfolio : null}
            scrollContainerRefs={scrollContainerRefs}
            onScroll={scrollCategory}
          />
        )}
      </div>

      {/* Add to Portfolio Modal */}
      {showAddModal && selectedStockForAdd && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modern-modal" role="dialog" aria-modal="true" aria-label={`${selectedStockForAdd.name} zum Portfolio hinzufügen`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-grabber" aria-hidden="true"></div>
            <h3>{selectedStockForAdd.name}</h3>
            <p className="sector-info">{selectedStockForAdd.sector}</p>

            <div className="quantity-input">
              <div className="label-with-info">
                <label htmlFor="add-quantity">Anzahl:</label>
                <div className="tooltip">
                  <i className="fas fa-question-circle info-icon" aria-hidden="true"></i>
                  <span className="tooltip-text">Die Anzahl der Aktien, die du kaufen möchtest.</span>
                </div>
              </div>
              <input
                id="add-quantity"
                type="number"
                min="1"
                value={addQuantity}
                onChange={(e) => setAddQuantity(parseInt(e.target.value) || 1)}
                className="modern-input"
                inputMode="numeric"
                placeholder="z.B. 10"
              />
            </div>

            <div className="quantity-input">
              <div className="label-with-info">
                <label htmlFor="add-purchase-date">Kaufdatum:</label>
                <div className="tooltip">
                  <i className="fas fa-question-circle info-icon" aria-hidden="true"></i>
                  <span className="tooltip-text">Das Datum, an dem du die Aktien gekauft hast.</span>
                </div>
              </div>
              <input
                id="add-purchase-date"
                type="date"
                value={addPurchaseDate}
                onChange={(e) => setAddPurchaseDate(e.target.value)}
                className="modern-input"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="modal-actions">
              <button type="button" onClick={confirmAddToPortfolio} className="action-button primary" disabled={isAddingStock}>
                {isAddingStock ? (
                  <><div className="spinner-small"></div> Wird hinzugefügt...</>
                ) : (
                  <><i className="fas fa-plus"></i> Zum Portfolio hinzufügen</>
                )}
              </button>
              <button type="button" onClick={closeAddModal} className="action-button secondary">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Search;
